import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  CalendarDays,
  Grid3X3,
  LayoutGrid,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { annualPlanningService, AcademicEvent, EventCategory } from '@/features/calendar/services/annualPlanningService';
import { holidaysService, PublicHoliday } from '@/features/calendar/services/holidaysService';
import { calendarSettingsService, AcademicYearTemplate, RamadanDates } from '@/features/calendar/services/calendarSettingsService';
import examScheduleService, { ExamPeriods } from '@/features/calendar/services/examScheduleService';
import { fetchCurrentAcademicYear } from '@/services/academicPlanningService';
import { format, isSameDay, isWithinInterval, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AcademicYear {
  id: string;
  name: string;
  isCurrent: boolean;
  startDate: string;
  endDate: string;
}

type ViewMode = 'yearly' | 'monthly';

// Categories visible to students - filter by visibility property
// STAFF_ONLY categories like ADMINISTRATIVE are excluded

const ALL_MONTHS = [
  { index: 0, name: 'Janvier', abbr: 'Jan' },
  { index: 1, name: 'Février', abbr: 'Fév' },
  { index: 2, name: 'Mars', abbr: 'Mar' },
  { index: 3, name: 'Avril', abbr: 'Avr' },
  { index: 4, name: 'Mai', abbr: 'Mai' },
  { index: 5, name: 'Juin', abbr: 'Juin' },
  { index: 6, name: 'Juillet', abbr: 'Juil' },
  { index: 7, name: 'Août', abbr: 'Août' },
  { index: 8, name: 'Septembre', abbr: 'Sept' },
  { index: 9, name: 'Octobre', abbr: 'Oct' },
  { index: 10, name: 'Novembre', abbr: 'Nov' },
  { index: 11, name: 'Décembre', abbr: 'Déc' },
];

interface MonthInfo {
  index: number;
  name: string;
  abbr: string;
  year: number;
}

interface EventBar {
  event: AcademicEvent | PublicHoliday;
  type: 'event' | 'holiday' | 'marker';
  startDay: number;
  endDay: number;
  color: string;
  title: string;
  lane: number;
  categoryLabel?: string;
}

const MyCalendarPage: React.FC = () => {
  const { tokens } = useAuthStore();
  const [viewMode, setViewMode] = useState<ViewMode>('yearly');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [academicYearStart, setAcademicYearStart] = useState(new Date().getFullYear());
  const [events, setEvents] = useState<AcademicEvent[]>([]);
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [academicYear, setAcademicYear] = useState<AcademicYear | null>(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<AcademicEvent | null>(null);
  const [template, setTemplate] = useState<AcademicYearTemplate | null>(null);
  const [ramadanDates, setRamadanDates] = useState<RamadanDates | null>(null);
  const [examPeriods, setExamPeriods] = useState<ExamPeriods | null>(null);
  const [hoveredEvent, setHoveredEvent] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number; showAbove: boolean } | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Load academic year template
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const data = await calendarSettingsService.getAcademicYearTemplate();
        setTemplate(data);
      } catch (error) {
        console.error('Failed to load academic year template:', error);
      }
    };
    loadTemplate();
  }, []);

  // Load Ramadan dates
  useEffect(() => {
    const loadRamadan = async () => {
      try {
        const ramadan1 = await calendarSettingsService.getRamadanDatesForYear(academicYearStart);
        const ramadan2 = await calendarSettingsService.getRamadanDatesForYear(academicYearStart + 1);
        setRamadanDates(ramadan2 || ramadan1);
      } catch (error) {
        console.error('Failed to load Ramadan dates:', error);
      }
    };
    loadRamadan();
  }, [academicYearStart]);

  const academicStartMonth = useMemo(() => {
    return template?.startMonth ? template.startMonth - 1 : 8; // Default to September (8)
  }, [template]);

  // Convert exam periods to academic events for display in calendar
  const examPeriodEvents = useMemo((): AcademicEvent[] => {
    if (!examPeriods || !academicYear) return [];

    const periodEvents: AcademicEvent[] = [];
    const tenantId = localStorage.getItem('nexedu_tenant_id') || '';

    // Find the ACADEMIC category for exam periods
    const academicCategory = categories.find(c => c.code === 'ACADEMIC');

    // Helper to create an event
    const createPeriodEvent = (
      id: string,
      title: string,
      startDate: string | null,
      endDate: string | null,
      color: string
    ): AcademicEvent | null => {
      if (!startDate || !endDate) return null;
      return {
        id,
        tenantId,
        academicYearId: academicYear.id,
        categoryId: academicCategory?.id,
        title,
        description: `Période définie dans les paramètres des examens`,
        eventType: 'EXAM_PERIOD',
        startDate,
        endDate,
        isAllDay: true,
        isRecurring: false,
        color,
        visibility: 'STUDENTS_AND_STAFF', // Students can see exam periods
        isAcademic: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        category: academicCategory,
      };
    };

    // Add exam period 1
    const period1Event = createPeriodEvent(
      'exam-period-1',
      examPeriods.examPeriodType === 'SINGLE' ? 'Période d\'examens' : 'Examens Semestre 1',
      examPeriods.period1?.start || null,
      examPeriods.period1?.end || null,
      '#F97316' // Orange
    );
    if (period1Event) periodEvents.push(period1Event);

    // Add exam period 2 (only for PER_SEMESTER)
    if (examPeriods.examPeriodType === 'PER_SEMESTER' && examPeriods.period2) {
      const period2Event = createPeriodEvent(
        'exam-period-2',
        'Examens Semestre 2',
        examPeriods.period2.start,
        examPeriods.period2.end,
        '#F97316' // Orange
      );
      if (period2Event) periodEvents.push(period2Event);
    }

    // Add rattrapage periods (only if rattrapage is enabled)
    if (examPeriods.rattrapageEnabled) {
      // Rattrapage period 1
      const rattrapage1Event = createPeriodEvent(
        'exam-rattrapage-1',
        examPeriods.rattrapagePeriodType === 'SINGLE' ? 'Période de rattrapage' : 'Rattrapage Semestre 1',
        examPeriods.rattrapage1?.start || null,
        examPeriods.rattrapage1?.end || null,
        '#EAB308' // Yellow/Amber
      );
      if (rattrapage1Event) periodEvents.push(rattrapage1Event);

      // Rattrapage period 2 (only for PER_SEMESTER)
      if (examPeriods.rattrapagePeriodType === 'PER_SEMESTER' && examPeriods.rattrapage2) {
        const rattrapage2Event = createPeriodEvent(
          'exam-rattrapage-2',
          'Rattrapage Semestre 2',
          examPeriods.rattrapage2.start,
          examPeriods.rattrapage2.end,
          '#EAB308' // Yellow/Amber
        );
        if (rattrapage2Event) periodEvents.push(rattrapage2Event);
      }
    }

    return periodEvents;
  }, [examPeriods, academicYear, categories]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      if (!tokens?.accessToken) return;

      setIsLoading(true);
      setError(null);

      try {
        // Load current academic year (students can only see current year)
        const year = await fetchCurrentAcademicYear(tokens.accessToken);
        if (year) {
          setAcademicYear(year);
          // Set the year based on academic year start date
          setAcademicYearStart(new Date(year.startDate).getFullYear());
        }

        // Load categories
        const cats = await annualPlanningService.getCategories();
        // Filter to only student-visible categories based on visibility property
        // This excludes STAFF_ONLY categories like ADMINISTRATIVE
        const visibleCats = cats.filter((c) =>
          c.visibility === 'ALL' || c.visibility === 'STUDENTS_AND_STAFF'
        );
        setCategories(visibleCats);
        setSelectedCategoryIds(visibleCats.map((c) => c.id));
      } catch (err) {
        console.error('Failed to load initial data:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [tokens?.accessToken]);

  // Load events when academic year changes
  useEffect(() => {
    const loadEvents = async () => {
      if (!academicYear?.id) return;

      try {
        // Get the year from academic year start date for holidays
        const academicStartYear = new Date(academicYear.startDate).getFullYear();
        const academicEndYear = new Date(academicYear.endDate).getFullYear();

        const [eventsData, holidaysYear1, holidaysYear2, periods] = await Promise.all([
          annualPlanningService.getEvents({ academicYearId: academicYear.id }),
          holidaysService.getHolidays(academicStartYear),
          academicStartYear !== academicEndYear
            ? holidaysService.getHolidays(academicEndYear)
            : Promise.resolve([]),
          examScheduleService.getExamPeriods(academicYear.id).catch(() => null),
        ]);

        // Filter events by visibility (students can see ALL and STUDENTS_AND_STAFF)
        const visibleEvents = eventsData.filter(
          (e: AcademicEvent) => e.visibility === 'ALL' || e.visibility === 'STUDENTS_AND_STAFF'
        );

        // Combine holidays from both years
        const allHolidays = [...holidaysYear1, ...holidaysYear2];

        setEvents(visibleEvents);
        setHolidays(allHolidays);
        setExamPeriods(periods);
      } catch (err) {
        console.error('Failed to load events:', err);
      }
    };

    loadEvents();
  }, [academicYear?.id, academicYear?.startDate, academicYear?.endDate]);

  // Academic year markers derived from template
  const academicMarkers = useMemo(() => {
    if (!template) return [];

    const academicCategory = categories.find(c => c.code === 'ACADEMIC');
    const academicColor = academicCategory?.color || '#3B82F6';

    const markers: { date: Date; label: string; color: string; type: string }[] = [];
    const getYearForMonth = (monthIndex: number): number => {
      return monthIndex < academicStartMonth ? academicYearStart + 1 : academicYearStart;
    };

    if (template.startDay && template.startMonth) {
      const month = template.startMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.startDay),
        label: "Début Année Académique",
        color: academicColor,
        type: 'year-start'
      });
    }

    if (template.endDay && template.endMonth) {
      const month = template.endMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.endDay),
        label: "Fin Année Académique",
        color: academicColor,
        type: 'year-end'
      });
    }

    if (template.semester1StartDay && template.semester1StartMonth) {
      const month = template.semester1StartMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.semester1StartDay),
        label: "Début Semestre 1",
        color: academicColor,
        type: 'sem1-start'
      });
    }

    if (template.semester1EndDay && template.semester1EndMonth) {
      const month = template.semester1EndMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.semester1EndDay),
        label: "Fin Semestre 1",
        color: academicColor,
        type: 'sem1-end'
      });
    }

    if (template.semester2StartDay && template.semester2StartMonth) {
      const month = template.semester2StartMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.semester2StartDay),
        label: "Début Semestre 2",
        color: academicColor,
        type: 'sem2-start'
      });
    }

    if (template.semester2EndDay && template.semester2EndMonth) {
      const month = template.semester2EndMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.semester2EndDay),
        label: "Fin Semestre 2",
        color: academicColor,
        type: 'sem2-end'
      });
    }

    return markers;
  }, [template, academicYearStart, academicStartMonth, categories]);

  const getYearForMonth = (monthIndex: number): number => {
    return monthIndex < academicStartMonth ? academicYearStart + 1 : academicYearStart;
  };

  const allMonths: MonthInfo[] = useMemo(() => {
    const months: MonthInfo[] = [];
    for (let i = 0; i < 12; i++) {
      const monthIndex = (academicStartMonth + i) % 12;
      const year = getYearForMonth(monthIndex);
      months.push({ ...ALL_MONTHS[monthIndex], year });
    }
    return months;
  }, [academicYearStart, academicStartMonth]);

  const maxDays = 31;

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();

  // Allocate lanes for events
  const allocateLanes = (bars: Omit<EventBar, 'lane'>[]): EventBar[] => {
    if (bars.length === 0) return [];

    const sortedBars = [...bars].sort((a, b) => {
      if (a.startDay !== b.startDay) return a.startDay - b.startDay;
      return (b.endDay - b.startDay) - (a.endDay - a.startDay);
    });

    const lanes: number[][] = [];

    return sortedBars.map(bar => {
      let assignedLane = -1;
      for (let laneIdx = 0; laneIdx < lanes.length; laneIdx++) {
        const lane = lanes[laneIdx];
        let canFit = true;
        for (let day = bar.startDay; day <= bar.endDay; day++) {
          if (lane.includes(day)) {
            canFit = false;
            break;
          }
        }
        if (canFit) {
          assignedLane = laneIdx;
          break;
        }
      }

      if (assignedLane === -1) {
        assignedLane = lanes.length;
        lanes.push([]);
      }

      for (let day = bar.startDay; day <= bar.endDay; day++) {
        lanes[assignedLane].push(day);
      }

      return { ...bar, lane: assignedLane };
    });
  };

  // Get event bars for a specific month
  const getEventBarsForMonth = (monthInfo: MonthInfo): EventBar[] => {
    const bars: Omit<EventBar, 'lane'>[] = [];
    const monthStart = new Date(monthInfo.year, monthInfo.index, 1);
    const monthEnd = new Date(monthInfo.year, monthInfo.index + 1, 0);
    const daysInMonth = monthEnd.getDate();

    // Academic markers
    academicMarkers.forEach(marker => {
      if (marker.date.getMonth() === monthInfo.index && marker.date.getFullYear() === monthInfo.year) {
        bars.push({
          event: { id: marker.type, name: marker.label } as unknown as PublicHoliday,
          type: 'marker',
          startDay: marker.date.getDate(),
          endDay: marker.date.getDate(),
          color: marker.color,
          title: marker.label,
          categoryLabel: 'Repère Académique',
        });
      }
    });

    // Holidays
    const vacationCategory = categories.find(c => c.code === 'VACATION');
    const holidayColor = vacationCategory?.color || '#10B981';
    const seenHolidays = new Set<string>();

    holidays.forEach(holiday => {
      if (seenHolidays.has(holiday.name)) return;

      const hStart = new Date(holiday.startDate);
      const hEnd = new Date(holiday.endDate);

      if (hEnd >= monthStart && hStart <= monthEnd) {
        seenHolidays.add(holiday.name);
        const startDay = hStart < monthStart ? 1 : hStart.getDate();
        const endDay = hEnd > monthEnd ? daysInMonth : hEnd.getDate();

        bars.push({
          event: holiday,
          type: 'holiday',
          startDay,
          endDay,
          color: holidayColor,
          title: holiday.name,
          categoryLabel: 'Période de Vacances',
        });
      }
    });

    // Combine regular events with exam period events
    const allEvents = [...events, ...examPeriodEvents];

    // Events
    allEvents.forEach(event => {
      // Filter by selected categories
      if (event.categoryId && !selectedCategoryIds.includes(event.categoryId)) return;

      const eStart = new Date(event.startDate);
      const eEnd = new Date(event.endDate);

      if (eEnd >= monthStart && eStart <= monthEnd) {
        const startDay = eStart < monthStart ? 1 : eStart.getDate();
        const endDay = eEnd > monthEnd ? daysInMonth : eEnd.getDate();

        const category = event.category || categories.find(c => c.id === event.categoryId);
        const color = event.color || category?.color || '#6B7280';

        bars.push({
          event,
          type: 'event',
          startDay,
          endDay,
          color,
          title: event.title,
          categoryLabel: category?.name || 'Événement',
        });
      }
    });

    return allocateLanes(bars);
  };

  // Check if a date is during Ramadan
  const isRamadanDay = (year: number, month: number, day: number): boolean => {
    if (!ramadanDates) return false;
    const date = new Date(year, month, day);
    const ramadanStart = new Date(ramadanDates.startDate);
    const ramadanEnd = new Date(ramadanDates.endDate);
    return date >= ramadanStart && date <= ramadanEnd;
  };

  const isWeekend = (year: number, month: number, day: number): boolean => {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  const isToday = (year: number, month: number, day: number): boolean => {
    const today = new Date();
    return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
  };

  // Monthly view helpers
  const monthStartDate = startOfMonth(currentMonth);
  const monthEndDate = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStartDate, end: monthEndDate });
  const startDayOfWeek = monthStartDate.getDay();
  const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

  const handlePreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const handleToday = () => setCurrentMonth(new Date());

  const goToCurrentAcademicYear = () => {
    const now = new Date();
    const currentMonthIdx = now.getMonth();
    const startYear = currentMonthIdx >= academicStartMonth ? now.getFullYear() : now.getFullYear() - 1;
    setAcademicYearStart(startYear);
  };

  // Get events for a specific date (for monthly view)
  const getEventsForDate = (date: Date) => {
    // Combine regular events with exam period events
    const allEvents = [...events, ...examPeriodEvents];

    const dayEvents = allEvents.filter((event) => {
      const start = parseISO(event.startDate);
      const end = parseISO(event.endDate);

      if (event.categoryId && !selectedCategoryIds.includes(event.categoryId)) {
        return false;
      }

      return isWithinInterval(date, { start, end }) || isSameDay(date, start) || isSameDay(date, end);
    });

    const dayHolidays = holidays.filter((holiday) => {
      const start = parseISO(holiday.startDate);
      const end = parseISO(holiday.endDate);
      return isWithinInterval(date, { start, end }) || isSameDay(date, start) || isSameDay(date, end);
    });

    return { events: dayEvents, holidays: dayHolidays };
  };

  // Toggle category filter
  const toggleCategory = (categoryId: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
            <CalendarDays className="text-white" size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Calendrier Académique</h1>
            <p className="text-sm text-gray-500">Événements et dates importantes</p>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0" size={18} />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* Top Bar: Academic Year + View Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Année Académique</span>
            <span className="text-sm font-semibold text-gray-900">
              {academicYear?.name || `${academicYearStart}/${academicYearStart + 1}`}
            </span>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('yearly')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'yearly'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3X3 size={16} />
              <span className="hidden sm:inline">Vue Annuelle</span>
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'monthly'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <LayoutGrid size={16} />
              <span className="hidden sm:inline">Vue Mensuelle</span>
            </button>
          </div>
        </div>
      </div>

      {/* Yearly View - TeamUp Style */}
      {viewMode === 'yearly' && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          {/* Navigation Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAcademicYearStart(academicYearStart - 1)}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setAcademicYearStart(academicYearStart + 1)}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              >
                <ChevronRight size={18} />
              </button>
              <button
                onClick={goToCurrentAcademicYear}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                Aujourd'hui
              </button>
              <span className="text-sm font-medium text-gray-700 ml-2">
                {allMonths[0]?.abbr} {allMonths[0]?.year} - {allMonths[11]?.abbr} {allMonths[11]?.year}
              </span>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => toggleCategory(category.id)}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-all ${
                    selectedCategoryIds.includes(category.id)
                      ? 'shadow-sm'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                  style={{
                    backgroundColor: selectedCategoryIds.includes(category.id)
                      ? `${category.color}20`
                      : undefined,
                    color: selectedCategoryIds.includes(category.id)
                      ? category.color
                      : undefined,
                  }}
                >
                  <div
                    className="w-2 h-2 rounded-sm"
                    style={{ backgroundColor: selectedCategoryIds.includes(category.id) ? category.color : '#9CA3AF' }}
                  />
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Calendar Grid - TeamUp Style */}
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white text-xs">
            {/* Days header row */}
            <div className="flex border-b border-gray-200 bg-gray-50">
              <div className="w-20 flex-shrink-0 px-2 py-1.5 font-medium text-gray-500 border-r border-gray-200" />
              <div className="flex-1 flex">
                {Array.from({ length: maxDays }, (_, i) => (
                  <div
                    key={i}
                    className="flex-1 py-1.5 text-center font-medium text-gray-500 border-r border-gray-100 last:border-r-0"
                    style={{ minWidth: '20px' }}
                  >
                    {i + 1}
                  </div>
                ))}
              </div>
            </div>

            {/* Month rows */}
            {allMonths.map((monthInfo) => {
              const daysInThisMonth = getDaysInMonth(monthInfo.year, monthInfo.index);
              const eventBars = getEventBarsForMonth(monthInfo);
              const maxLane = eventBars.length > 0 ? Math.max(...eventBars.map(b => b.lane)) + 1 : 0;

              return (
                <div
                  key={`${monthInfo.year}-${monthInfo.index}`}
                  className="flex border-b border-gray-100 last:border-b-0 relative"
                  style={{ minHeight: `${Math.max(24, maxLane * 18 + 6)}px` }}
                >
                  {/* Month label */}
                  <div className="w-20 flex-shrink-0 px-2 py-1 font-medium text-gray-700 border-r border-gray-200 bg-gray-50">
                    {monthInfo.name}
                  </div>

                  {/* Days grid with events */}
                  <div className="flex-1 relative">
                    {/* Day cells background */}
                    <div className="flex absolute inset-0">
                      {Array.from({ length: maxDays }, (_, dayIndex) => {
                        const day = dayIndex + 1;
                        const isValidDay = day <= daysInThisMonth;
                        const weekend = isValidDay && isWeekend(monthInfo.year, monthInfo.index, day);
                        const today = isValidDay && isToday(monthInfo.year, monthInfo.index, day);
                        const ramadan = isValidDay && isRamadanDay(monthInfo.year, monthInfo.index, day);

                        return (
                          <div
                            key={dayIndex}
                            className={`
                              flex-1 border-r border-gray-100 last:border-r-0 select-none
                              ${!isValidDay ? 'bg-gray-50' : ''}
                              ${today ? 'bg-blue-50' : ramadan && isValidDay ? 'bg-emerald-200/70' : weekend && isValidDay ? 'bg-gray-50' : ''}
                            `}
                            style={{ minWidth: '20px' }}
                          />
                        );
                      })}
                    </div>

                    {/* Event bars */}
                    <div className="relative z-10 py-0.5 px-0.5">
                      {eventBars.map((bar, idx) => {
                        const leftPercent = ((bar.startDay - 1) / maxDays) * 100;
                        const widthPercent = ((bar.endDay - bar.startDay + 1) / maxDays) * 100;
                        const isHovered = hoveredEvent === `${monthInfo.year}-${monthInfo.index}-${idx}`;
                        const startDate = new Date(monthInfo.year, monthInfo.index, bar.startDay);
                        const endDate = new Date(monthInfo.year, monthInfo.index, bar.endDay);

                        const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const viewportHeight = window.innerHeight;
                          const showAbove = rect.bottom > viewportHeight / 2;
                          setHoveredEvent(`${monthInfo.year}-${monthInfo.index}-${idx}`);
                          setTooltipPosition({
                            x: rect.left + rect.width / 2,
                            y: showAbove ? rect.top : rect.bottom,
                            showAbove,
                          });
                        };

                        const handleMouseLeave = () => {
                          setHoveredEvent(null);
                          setTooltipPosition(null);
                        };

                        return (
                          <div
                            key={idx}
                            className={`absolute ${isHovered ? 'z-[100]' : ''}`}
                            style={{
                              left: `${leftPercent}%`,
                              width: `${widthPercent}%`,
                              top: `${bar.lane * 18 + 2}px`,
                            }}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                          >
                            <div
                              className={`
                                h-4 rounded-sm text-[10px] font-medium text-white px-1 truncate
                                transition-all duration-100 cursor-pointer
                                ${isHovered ? 'shadow-lg ring-2 ring-white/50' : ''}
                              `}
                              style={{
                                backgroundColor: bar.color,
                                opacity: isHovered ? 1 : 0.9,
                              }}
                              onClick={() => {
                                if (bar.type === 'event') {
                                  setSelectedEvent(bar.event as AcademicEvent);
                                }
                              }}
                            >
                              {bar.title}
                            </div>

                            {/* Hover tooltip */}
                            {isHovered && tooltipPosition && createPortal(
                              <div
                                ref={tooltipRef}
                                className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[200px] max-w-[280px] pointer-events-none"
                                style={{
                                  left: tooltipPosition.x,
                                  top: tooltipPosition.showAbove ? 'auto' : tooltipPosition.y + 8,
                                  bottom: tooltipPosition.showAbove ? window.innerHeight - tooltipPosition.y + 8 : 'auto',
                                  transform: 'translateX(-50%)',
                                }}
                              >
                                <div className="text-sm font-semibold text-gray-900 mb-1">
                                  {bar.title}
                                </div>
                                <div className="text-xs text-gray-500 mb-2">
                                  {startDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                  {bar.startDay !== bar.endDay && ` - ${endDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`}
                                  {' '}{monthInfo.year}
                                </div>
                                <div
                                  className="text-xs px-2 py-1 rounded inline-block font-medium"
                                  style={{
                                    backgroundColor: bar.color + '20',
                                    color: bar.color,
                                  }}
                                >
                                  {bar.categoryLabel}
                                </div>
                                <div
                                  className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-l-transparent border-r-transparent ${
                                    tooltipPosition.showAbove
                                      ? 'top-full border-t-[6px] border-t-white'
                                      : 'bottom-full border-b-[6px] border-b-white'
                                  }`}
                                />
                              </div>,
                              document.body
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expandable Legend Sections */}
          <div className="space-y-2 mt-4">
            {categories.filter(c => selectedCategoryIds.includes(c.id)).map(category => {
              const isVacationCategory = category.code === 'VACATION';
              const isAcademicCategory = category.code === 'ACADEMIC';

              // Include both regular events and exam period events
              const allEventsForCategory = [...events, ...examPeriodEvents];
              const categoryEvents = allEventsForCategory.filter(e =>
                (e.categoryId === category.id) || (e.category?.id === category.id) || (e.category?.code === category.code)
              );

              const allItems: Array<{ id: string; title: string; startDate: string; isHoliday: boolean; isMarker?: boolean }> = [];

              const academicYearStartDate = new Date(academicYearStart, academicStartMonth, 1);
              const academicYearEndDate = new Date(academicYearStart + 1, academicStartMonth, 0);

              if (isAcademicCategory) {
                academicMarkers.forEach(marker => {
                  allItems.push({
                    id: `marker-${marker.type}`,
                    title: marker.label,
                    startDate: marker.date.toISOString(),
                    isHoliday: false,
                    isMarker: true,
                  });
                });
              }

              if (isVacationCategory) {
                const seenHolidayKeys = new Set<string>();
                holidays.forEach(h => {
                  const holidayDate = new Date(h.startDate);
                  const holidayTime = new Date(holidayDate.getFullYear(), holidayDate.getMonth(), holidayDate.getDate()).getTime();
                  const startTime = academicYearStartDate.getTime();
                  const endTime = academicYearEndDate.getTime();

                  if (holidayTime >= startTime && holidayTime <= endTime) {
                    const dateKey = `${holidayDate.getFullYear()}-${holidayDate.getMonth()}-${holidayDate.getDate()}`;
                    const uniqueKey = `${h.name}-${dateKey}`;

                    if (!seenHolidayKeys.has(uniqueKey)) {
                      seenHolidayKeys.add(uniqueKey);
                      allItems.push({
                        id: `holiday-${h.id}`,
                        title: h.name,
                        startDate: h.startDate,
                        isHoliday: true,
                      });
                    }
                  }
                });
              }

              categoryEvents.forEach(e => {
                const eventDate = new Date(e.startDate);
                const eventTime = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate()).getTime();
                const startTime = academicYearStartDate.getTime();
                const endTime = academicYearEndDate.getTime();

                if (eventTime >= startTime && eventTime <= endTime) {
                  allItems.push({
                    id: e.id,
                    title: e.title,
                    startDate: e.startDate,
                    isHoliday: false,
                  });
                }
              });

              if (allItems.length === 0) return null;

              const sortedItems = [...allItems].sort((a, b) => {
                const dateA = new Date(a.startDate);
                const dateB = new Date(b.startDate);

                const getAcademicMonthIndex = (date: Date) => {
                  const month = date.getMonth();
                  return month >= academicStartMonth ? month - academicStartMonth : month + (12 - academicStartMonth);
                };

                const academicMonthA = getAcademicMonthIndex(dateA);
                const academicMonthB = getAcademicMonthIndex(dateB);

                if (academicMonthA !== academicMonthB) {
                  return academicMonthA - academicMonthB;
                }
                return dateA.getDate() - dateB.getDate();
              });

              return (
                <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => {
                      const newSet = new Set(expandedSections);
                      if (newSet.has(category.id)) {
                        newSet.delete(category.id);
                      } else {
                        newSet.add(category.id);
                      }
                      setExpandedSections(newSet);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: category.color }} />
                      <span className="text-sm font-medium text-gray-700">{category.name}</span>
                      <span className="text-xs text-gray-500">({allItems.length})</span>
                    </div>
                    {expandedSections.has(category.id) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {expandedSections.has(category.id) && (
                    <div className="px-3 py-2 space-y-1 bg-white max-h-64 overflow-y-auto">
                      {sortedItems.map(item => (
                        <div
                          key={item.id}
                          className={`flex items-center gap-2 text-xs py-1 rounded px-1 -mx-1 ${
                            !item.isHoliday && !item.isMarker ? 'cursor-pointer hover:bg-gray-50' : ''
                          }`}
                          onClick={() => {
                            if (!item.isHoliday && !item.isMarker) {
                              const event = categoryEvents.find(e => e.id === item.id);
                              if (event) setSelectedEvent(event);
                            }
                          }}
                        >
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: category.color }}
                          />
                          <span className="text-gray-600 w-28 flex-shrink-0">
                            {new Date(item.startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                          <span className="text-gray-800 font-medium truncate">{item.title}</span>
                          {item.isHoliday && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0"
                              style={{
                                backgroundColor: category.color + '20',
                                color: category.color
                              }}
                            >Férié</span>
                          )}
                          {item.isMarker && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0"
                              style={{
                                backgroundColor: category.color + '20',
                                color: category.color
                              }}
                            >Repère</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Ramadan indicator */}
            {ramadanDates && (
              <div className="flex items-center gap-2 px-3 py-2 text-xs bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="w-3 h-3 rounded-sm bg-emerald-300" />
                <span className="text-emerald-700 font-medium">Ramadan:</span>
                <span className="text-emerald-600">
                  {new Date(ramadanDates.startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} - {new Date(ramadanDates.endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Monthly View */}
      {viewMode === 'monthly' && (
        <>
          {/* Category Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => toggleCategory(category.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedCategoryIds.includes(category.id)
                      ? 'shadow-sm'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                  style={{
                    backgroundColor: selectedCategoryIds.includes(category.id)
                      ? `${category.color}20`
                      : undefined,
                    color: selectedCategoryIds.includes(category.id)
                      ? category.color
                      : undefined,
                  }}
                >
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: selectedCategoryIds.includes(category.id) ? category.color : '#9CA3AF' }}
                  />
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Calendar Navigation */}
          <div className="flex items-center justify-between mb-4 bg-white rounded-xl border border-gray-200 px-4 py-2">
            <button
              onClick={handlePreviousMonth}
              className="flex items-center gap-1 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline">Mois précédent</span>
            </button>

            <div className="flex items-center gap-3">
              <span className="text-base font-semibold text-gray-900 capitalize">
                {format(currentMonth, 'MMMM yyyy', { locale: fr })}
              </span>
              <button
                onClick={handleToday}
                className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                Aujourd'hui
              </button>
            </div>

            <button
              onClick={handleNextMonth}
              className="flex items-center gap-1 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
            >
              <span className="hidden sm:inline">Mois suivant</span>
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Monthly Calendar Grid */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
              {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
                <div key={day} className="p-2 text-center text-xs font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7">
              {/* Empty cells for padding */}
              {Array.from({ length: adjustedStartDay }).map((_, i) => (
                <div key={`empty-${i}`} className="min-h-[100px] border-b border-r border-gray-100 bg-gray-50/50" />
              ))}

              {/* Month days */}
              {monthDays.map((day) => {
                const { events: dayEvents, holidays: dayHolidays } = getEventsForDate(day);
                const isDayToday = isSameDay(day, new Date());
                const isDayWeekend = day.getDay() === 0 || day.getDay() === 6;

                return (
                  <div
                    key={day.toISOString()}
                    className={`min-h-[100px] border-b border-r border-gray-100 p-1.5 ${
                      isDayWeekend ? 'bg-gray-50/50' : ''
                    } ${isDayToday ? 'bg-blue-50/50' : ''}`}
                  >
                    {/* Day Number */}
                    <div
                      className={`text-sm font-medium mb-1 ${
                        isDayToday
                          ? 'text-blue-700 bg-blue-100 w-6 h-6 rounded-full flex items-center justify-center'
                          : 'text-gray-700'
                      }`}
                    >
                      {format(day, 'd')}
                    </div>

                    {/* Events */}
                    <div className="space-y-0.5">
                      {/* Holidays */}
                      {dayHolidays.slice(0, 1).map((holiday) => (
                        <div
                          key={holiday.id}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 truncate"
                          title={holiday.name}
                        >
                          {holiday.name}
                        </div>
                      ))}

                      {/* Events */}
                      {dayEvents.slice(0, 2 - dayHolidays.length).map((event) => (
                        <div
                          key={event.id}
                          className="text-[10px] px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80"
                          style={{
                            backgroundColor: `${event.category?.color || event.color || '#3B82F6'}20`,
                            color: event.category?.color || event.color || '#3B82F6',
                          }}
                          title={event.title}
                          onClick={() => setSelectedEvent(event)}
                        >
                          {event.title}
                        </div>
                      ))}

                      {/* More indicator */}
                      {dayEvents.length + dayHolidays.length > 2 && (
                        <div className="text-[9px] text-gray-500 px-1">
                          +{dayEvents.length + dayHolidays.length - 2} autres
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Category Badge */}
              {selectedEvent.category && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium mb-3"
                  style={{
                    backgroundColor: `${selectedEvent.category.color}20`,
                    color: selectedEvent.category.color,
                  }}
                >
                  {selectedEvent.category.name}
                </span>
              )}

              <h2 className="text-xl font-bold text-gray-900 mb-2">{selectedEvent.title}</h2>

              {/* Dates */}
              <div className="flex items-center gap-2 text-gray-600 mb-4">
                <CalendarDays className="h-4 w-4" />
                <span>
                  {format(parseISO(selectedEvent.startDate), 'd MMMM yyyy', { locale: fr })}
                  {selectedEvent.startDate !== selectedEvent.endDate && (
                    <> - {format(parseISO(selectedEvent.endDate), 'd MMMM yyyy', { locale: fr })}</>
                  )}
                </span>
              </div>

              {/* Description */}
              {selectedEvent.description && (
                <p className="text-gray-700 mb-4">{selectedEvent.description}</p>
              )}

              {/* Campus */}
              {selectedEvent.campus && (
                <div className="text-sm text-gray-500 mb-4">
                  Campus: {selectedEvent.campus.name}
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => setSelectedEvent(null)}
                className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCalendarPage;
